import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { ObjectTypeResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import { filterObjectTypes, mapObjectTypeToSearchResult } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseObjectTypesSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type ObjectTypesSource = SearchSource<ObjectTypeResult>;

export function useObjectTypesSource({ workspaceId, enabled, query }: UseObjectTypesSourceOptions): ObjectTypesSource {
  const objectTypesQuery = useQuery(
    getHydraUIAPIQueryOptions({
      queryKey: ['getObjectTypes', { params: { environment_id: workspaceId } }],
      queryFn: ({ signal }) =>
        hydraUiApi.objects.getObjectTypes({
          params: { path: { environment_id: String(workspaceId) } },
          signal,
        }),
      queryConfig: {
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
        select: (data) => data.object_types,
      },
    }),
  );

  const results = useMemo(
    () =>
      filterObjectTypes(objectTypesQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((objectType) =>
        mapObjectTypeToSearchResult(objectType, workspaceId),
      ),
    [objectTypesQuery.data, query, workspaceId],
  );

  return { ...objectTypesQuery, results };
}
